import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { STORAGE_KEYS } from '@/constants/storageKeys';

type AccountLinkingContext = {
  duplicateUserId: number;
  duplicateIdToken: string;
  maskedEmail: string;
  authProvider: 'password' | 'google';
  createdAt: number;
};

const KEY = STORAGE_KEYS.ACCOUNT_LINKING_CONTEXT;

/**
 * Storage adapter that maintains exact same behavior as original implementation:
 * - Web: Always uses sessionStorage (regardless of authentication)
 * - Native: Always uses AsyncStorage
 */
const getAccountLinkingStorage = () => {
  if (isWeb && typeof window !== 'undefined') {
    return {
      async getItem(key: string) {
        return window.sessionStorage.getItem(key);
      },
      async setItem(key: string, value: string) {
        window.sessionStorage.setItem(key, value);
      },
      async removeItem(key: string) {
        window.sessionStorage.removeItem(key);
      },
    };
  }

  return {
    async getItem(key: string) {
      return AsyncStorage.getItem(key);
    },
    async setItem(key: string, value: string) {
      return AsyncStorage.setItem(key, value);
    },
    async removeItem(key: string) {
      return AsyncStorage.removeItem(key);
    },
  };
};

export async function saveAccountLinkingContext(context: AccountLinkingContext) {
  const storage = getAccountLinkingStorage();

  const payload = JSON.stringify(context);

  await storage.setItem(KEY, payload);
}

export async function loadAccountLinkingContext(): Promise<AccountLinkingContext | null> {
  const storage = getAccountLinkingStorage();

  const raw = await storage.getItem(KEY);

  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as AccountLinkingContext;
  } catch {
    return null;
  }
}

/**
 * Reports whether a linking context exists, without waiting a tick. Web only.
 *
 * The async read wraps `sessionStorage`, which is already synchronous — the promise is the
 * only thing that takes time. That tick is visible on web: a guard that renders nothing
 * while it waits lets the router tear down the navigator beneath it and rewrite the
 * address bar to an unrelated route, so the wait shows up as a bounce through a screen
 * nobody asked for.
 *
 * Answers only the common case, which is the one worth the shortcut: a definite "nothing
 * stored" lets a guard decide immediately, while anything else defers to the async read
 * that also enforces the context's lifetime. Native always defers — there is no
 * synchronous storage there, and no address bar for the bounce to appear in.
 */
export function hasNoAccountLinkingContext(): boolean {
  if (!isWeb || typeof window === 'undefined') {
    return false;
  }

  try {
    return window.sessionStorage.getItem(KEY) === null;
  } catch {
    return false;
  }
}

/**
 * Clears account linking context from storage.
 *
 * Also clears any related OAuth completion flags to ensure clean state on web.
 */
export async function clearAccountLinkingContext() {
  const storage = getAccountLinkingStorage();

  await storage.removeItem(KEY);

  if (isWeb && typeof window !== 'undefined') {
    window.sessionStorage.removeItem('amplify_oauth_completed');
  }
}
